import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Select } from "barte-design-system";
import { CreditCardIcon } from "@phosphor-icons/react";
import { usePostHog } from '@posthog/react';

import { CardInfo, InputNumber } from '@shared/components'
import { TRACK_EVENTS } from '@payfac/constants';
import { formatPercentage } from "@shared/helpers";
import { useValidationErrors } from "@payfac/hooks";

import { objectShape } from "./modal-edit-debit.constants";

import type { ModalEditDebitProps } from "./modal-edit-debit.type";
import styles from "./modal-edit-debit.module.css";

export const ModalEditDebit = ({
    isOpen,
    onClose,
    data,
    onHandleSubmit,
    isLoading,
    companyRate,
    taxAggregateVersion = "2",
}: ModalEditDebitProps) => {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [dataForm, setDataForm] = useState(data);
    const [initEdit, setInitEdit] = useState(false);

    const { isValid, errors, onHandleListenErrors } = useValidationErrors(objectShape(t), dataForm);

    const onChangeValues = (key: keyof ModalEditDebitProps['data'], value: ModalEditDebitProps['data'][keyof ModalEditDebitProps['data']]) => {
        setDataForm({ ...dataForm, [key]: value } as ModalEditDebitProps['data']);
        setInitEdit(true);
    };

    const getMarkupValue = () => {
        if (dataForm?.value === null || dataForm?.value === undefined) return null;

        if (taxAggregateVersion === "2") {
            return (dataForm?.value || 0) - (companyRate || 0);
        }

        return dataForm?.value;
    };

    const getTotalValue = () => {
        if (dataForm?.value === null || dataForm?.value === undefined) return null;

        if (taxAggregateVersion === "2") {
            return dataForm?.value;
        }

        return (dataForm?.value || 0) + (companyRate || 0);
    };

    const handleMarkupChange = (val: number | string | null) => {
        if (val === null || val === undefined || `${val}` === 'NaN') {
            onChangeValues("value", val as number);
            return;
        }
        if (taxAggregateVersion === "2") {
            onChangeValues("value", +(val) + (companyRate || 0));
        } else {
            onChangeValues("value", +val);
        }
    };

    const handleTotalChange = (val: number | string | null) => {
        if (val === null || val === undefined || `${val}` === 'NaN') {
            onChangeValues("value", val as number);
            return;
        }
        if (taxAggregateVersion === "2") {
            onChangeValues("value", +val);
        } else {
            onChangeValues("value", +(val) - (companyRate || 0));
        }
    };

    const handleSave = () => {
        if (!isValid) {
            onHandleListenErrors(true);
            return;
        }

        onHandleSubmit(dataForm);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.DEBIT_CLICKED);
    };

    const title = <div className={styles["modal-header"]}><CreditCardIcon /> {t("payfac.pageRatePlanModalEditDebitTitle")}</div> as unknown as string
    const hasDiff = JSON.stringify(dataForm) !== JSON.stringify(data);

    useEffect(() => {
        if (isOpen) {
            if (!initEdit) {
                setDataForm(data);
            }
        } else {
            onHandleListenErrors(false);
            setDataForm({} as ModalEditDebitProps['data']);
            setInitEdit(false);
        }
    }, [isOpen, data, initEdit, onHandleListenErrors]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="lg"
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: handleSave,
                    disabled: !hasDiff,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <CardInfo
                    title={null}
                    info={[
                        {
                            label: t("payfac.pageRatePlanModalRepassTimeLabel"),
                            value: (
                                <InputNumber
                                    value={dataForm?.delayForRedeem}
                                    onChange={(value) => onChangeValues("delayForRedeem", value)}
                                    prefix="D + "
                                    className={styles["repass-input-number"]}
                                    decimalPrecision={0}
                                    error={errors?.delayForRedeem}
                                    helperText={errors?.delayForRedeem || ''}
                                />
                            )
                        },
                        {
                            label: t("payfac.pageRatePlanModalRateTypeLabel"),
                            value: (
                                <Select
                                    options={[
                                        { value: "PERCENTAGE", label: t("payfac.pageRatePlanDetailsPercentage") },
                                        { value: "FIXED", label: t("payfac.pageRatePlanDetailsFixed") },
                                    ]}
                                    value={dataForm?.rateType || "PERCENTAGE"}
                                    onChange={() => null}
                                    disabled
                                    hidePrefixIcon
                                />
                            )
                        }
                    ]}
                />

                <div className={styles.hr} />

                <div className={styles['rates-container']}>
                    <div className={styles['network-title']}>
                        {t("payfac.pageRatePlanDetailsRates")}
                    </div>
                    <ul className={styles.rates}>
                        <li className={styles['rates-header']}>
                            <div className={styles['rates-col-header']}>{t("payfac.pageRatePlanModalTableColumnMarkup")}</div>
                            <div className={styles['rates-col-header']}>{t("payfac.pageRatePlanModalTableColumnBarte")}</div>
                            <div className={styles['rates-col-header']}>{t("payfac.pageRatePlanModalTableColumnTotal")}</div>
                        </li>

                        <li className={styles['rates-row']}>
                            <div className={styles['rates-col']}>
                                <InputNumber
                                    value={getMarkupValue() as number}
                                    onChange={handleMarkupChange}
                                    decimalPrecision={2}
                                    className={styles['input-number']}
                                    suffix="%"
                                    error={errors?.value}
                                />
                            </div>

                            <div className={styles['rates-col']}>
                                {formatPercentage(companyRate || 0)}
                            </div>

                            <div className={styles['rates-col']}>
                                <InputNumber
                                    value={getTotalValue() as number}
                                    onChange={handleTotalChange}
                                    decimalPrecision={2}
                                    className={styles['input-number']}
                                    suffix="%"
                                    error={errors?.value}
                                />
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </Modal>
    );
};
